import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../i18n";

const Field = ({ id, label, type = "text", value, onChange, required, textarea }) => (
  <div className="relative">
    <label htmlFor={id} className="block text-xs font-bold uppercase tracking-widest text-wanm-dark/50 mb-2">
      {label}{required && <span className="text-wanm-red"> *</span>}
    </label>
    {textarea ? (
      <textarea id={id} data-testid={`field-${id}`} value={value} onChange={onChange} required={required} rows={4}
        className="w-full bg-transparent border-b-2 border-wanm-dark/20 py-2.5 text-wanm-dark focus:outline-none focus:border-wanm-pink transition-colors resize-none" />
    ) : (
      <input id={id} type={type} data-testid={`field-${id}`} value={value} onChange={onChange} required={required}
        className="w-full bg-transparent border-b-2 border-wanm-dark/20 py-2.5 text-wanm-dark focus:outline-none focus:border-wanm-pink transition-colors" />
    )}
  </div>
);

// fields: array of {id, type?, textarea?, required?}
export const LeadForm = ({ endpoint, fields, submitLabel, extra = {}, testId = "lead-form" }) => {
  const { t } = useLang();
  const [form, setForm] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [err, setErr] = useState("");

  const set = (id) => (e) => setForm((p) => ({ ...p, [id]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending"); setErr("");
    try {
      await api.post(endpoint, { ...form, ...extra });
      setStatus("success");
      setForm({});
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || t.form.error);
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="rounded-2xl bg-wanm-offwhite p-12 text-center" data-testid={`${testId}-success`}>
        <div className="w-16 h-16 rounded-full wanm-gradient-bg flex items-center justify-center mx-auto mb-6">
          <Check className="text-white" size={30} />
        </div>
        <p className="text-2xl font-black text-wanm-dark">{t.form.success}</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-7" data-testid={testId}>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
        {fields.map((f) => (
          <div key={f.id} className={f.textarea ? "sm:col-span-2" : ""}>
            <Field id={f.id} label={t.form[f.id]} type={f.type} textarea={f.textarea}
              required={f.required} value={form[f.id] || ""} onChange={set(f.id)} />
          </div>
        ))}
      </div>
      {status === "error" && <p className="text-wanm-red text-sm font-bold" data-testid={`${testId}-error`}>{err}</p>}
      <button type="submit" disabled={status === "sending"} data-testid={`${testId}-submit`}
        className="inline-flex items-center gap-2 wanm-gradient-bg text-white font-bold rounded-full px-9 py-4 hover:scale-[1.03] transition-transform disabled:opacity-60">
        {status === "sending" ? t.form.sending : submitLabel}
      </button>
    </form>
  );
};

export default LeadForm;
